<script>
import {defineComponent, onMounted, getCurrentInstance, watch} from 'vue';
import msCombobox from './MsCombobox.vue';
import {useValidateControl} from '@/setup/validateControl.js';
import {MSConstant} from '@/commons/wildcards';
import i18nGlobal from '@/i18ns/i18n';
const i18n = i18nGlobal.global;

import commonFn from '@/commons/commonFunction';

export default defineComponent({
  name: 'MsComboboxEnum',
  extends: msCombobox,
  props: {
    enum: {
      default: null,
      type: String,
    },
    /**
     * @override
     */
    valueField: {
      type: String,
      default: 'enumValue',
    },
    /**
     * @override
     */
    displayField: {
      type: String,
      default: 'enumText',
    },

    values: {
      type: [Array, String],
      default: '',
    },

    /**
     * Custom dữ liệu
     */
    customData: {
      default: null,
      type: [Array, Function],
    },
    sortEnum: {
      type: [Boolean, String],
      default: false,
    },
    isGridEditor: {
      type: Boolean,
      default: false,
    },

    ignoreKeys: {
      type: [Array, String],
      default: [],
    },
    /**
     * Type combo
     */
    formatType: {
      type: [Number, String],
      default: 16,
    },
    /**
     * Tùy chọn có nhập dữ liệu input có tiền kiếm load grid không
     */
    filterSeachGrid: {
      type: Boolean,
      default: false,
    },
  },

  setup(props) {
    // Variable
    const {proxy} = getCurrentInstance();

    const {errorMessage, handlerValidateCombobox, getTitleError, clearValidate} = useValidateControl({
      props,
    });
    const validate = () => {
      if (!props.isGridEditor) {
        return handlerValidateCombobox();
      }
      return '';
    };
    // Hooks
    onMounted(() => {
      loadEnumSource();
    });

    /**
     * Sử dụng watch này để có thể thay đổi Value ComboboxEnum
     * HQMINH 13.10.2022
     */
    watch(
      () => proxy.values,
      (newValue, oldValue) => {
        if (newValue !== oldValue) {
          loadEnumSource();
        }
      },
    );
    // Method
    /**
     * Bổ sung values theo dạng text hoặc mảng để có thể filter theo enum
     * NCPHUOC 15/07/2022
     */
    const loadEnumSource = () => {
      let source = proxy.$ms.commonFn.getEnumSource(proxy.enum, proxy.values);
      if (proxy.sortEnum) {
        if (proxy.sortEnum == true || proxy.sortEnum == 'asc') {
          source = source.sort((a, b) => a.enumText - b.enumText || a.enumText.localeCompare(b.enumText));
        } else if (proxy.sortEnum == 'desc') {
          source = source.sort((a, b) => b.enumText - a.enumText || b.enumText.localeCompare(a.enumText));
        }
      }
      if (Array.isArray(source)) {
        // Loại bỏ các key thiết lập ignore
        let ignoreKeyArr = props.ignoreKeys;

        if (typeof props.ignoreKeys == 'string' && props.ignoreKeys) {
          ignoreKeyArr = props.ignoreKeys.split(',');
        }

        if (Array.isArray(ignoreKeyArr) && ignoreKeyArr.length > 0) {
          source = source.filter(e => !ignoreKeyArr.includes(e.enumKey));
        }

        let filterItems = [];
        if (Array.isArray(props.values)) {
          filterItems = props.values;
        } else {
          if (props.values) {
            filterItems = props.values.split(',');
          }
        }

        if (filterItems.length > 0) {
          const map = new Map();
          // Map lại để lấy cho nhanh
          filterItems.forEach(x => {
            map.set(x, 1);
          });
          source = source.filter(item => map.has(item.enumValue));
        }
      }

      /** Xử lý custom dữ liệu */
      if (proxy.customData) {
        if (Array.isArray(proxy.customData)) {
          source = proxy.customData.concat(source);
        }

        if (typeof proxy.customData == 'function') {
          source = proxy.customData(source);
        }
      }

      proxy._allData = source;

      proxy.internalDataSource = source;
    };

    /**
     * Override lại hàm này, thêm set internal value trên comboboxenum
     */
    const setInitText = val => {
      // gán lại giá trị cho internalText thỏa mãn điều kiện (sửa theo bug 29979)
      // ntban - 23.08.2023
      if (proxy.internalText) {
        proxy.internalText = proxy.initText ? proxy.initText : val;
      } else {
        proxy.internalText = val;
      }

      const sources = MSConstant[props.enum] || proxy.customData;
      if (Object.keys(sources)?.length > 0) {
        let value = null;
        if (props.enum) {
          for (const key in sources) {
            const enumText = i18n.t('i18nEnum.' + props.enum + '.' + key);
            if (val === enumText) {
              value = sources[key];
              break;
            }
          }
        }

        if (typeof value !== 'undefined' && value !== null) {
          proxy.setValueComboboxEnum(value); // ko gọi vào setValue nữa do hightlightItem đang có vấn đề, ko care được ảnh hưởng
        }
      }
    };

    const onClick = e => {
      if (proxy.selectOnly) {
        proxy.triggerClick();
      }
    };

    // Bổ sung watch để ko phải binding text vào
    watch(
      () => props.modelValue,
      (value, oldValue) => {
        let text = '';
        // Nếu value có giá trị => gán giá trị cho biến internalText để check trong hàm setInitText
        // ntban - 23.08.2023
        if (typeof value != 'undefined' || value != '') {
          if (typeof value === 'number') {
            text = commonFn.getEnumResource(value, props.enum);
          }
          proxy.internalText = value;
        }
        proxy.setInitText(text);
      },
    );

    /**
     * Load lại dữ liệu enum nếu ignoreKeys thay đổi
     * @author nvthinh 06.12.2023
     */
    watch(
      () => props.ignoreKeys,
      (newValue, oldValue) => {
        if (newValue !== oldValue) {
          loadEnumSource();
        }
      },
    );

    return {
      validate,
      errorMessage,
      getTitleError,
      onClick,
      setInitText,
      clearValidate,
    };
  },
});
</script>

<style lang="scss" scoped>
@import '@/assets/scss/components/MsCombobox.scss';
</style>
